import React, { useEffect } from 'react';
import { Phone, Mail, MapPin, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { companyInfo } from '../data/mock';

const ContactPage = () => {
  // SEO: Dynamic page title and meta description
  useEffect(() => {
    document.title = 'Kontakt | Entrümpelung anfragen | Helper-Schweiz ☎ +41 77 252 38 20';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Kontaktieren Sie Helper-Schweiz für Ihre Entrümpelung in der Deutschschweiz. Telefon: +41 77 252 38 20 | E-Mail: info@helper-schweiz.ch | WhatsApp verfügbar. Kostenlose Beratung!');
    }
    
    // Load Elfsight script for Google Reviews widget
    const scriptExists = document.querySelector('script[src="https://static.elfsight.com/platform/platform.js"]');
    if (!scriptExists) {
      const elfsightScript = document.createElement('script');
      elfsightScript.src = 'https://static.elfsight.com/platform/platform.js';
      elfsightScript.async = true;
      document.body.appendChild(elfsightScript);
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#f0f5fa] to-[#e8f0f8] py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-[#1e3a5f]">Kontakt</h1>
          <p className="mt-4 text-gray-600 text-lg max-w-2xl mx-auto">
            Nehmen Sie Kontakt mit mir auf – ich freue mich auf Ihre Anfrage!
          </p>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="bg-[#f8fafc] rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-[#1e3a5f] mb-6">Kontaktinformationen</h2>
              
              <div className="space-y-6">
                <div>
                  <p className="font-semibold text-[#1e3a5f] text-lg">{companyInfo.name}</p>
                  <p className="text-gray-600">{companyInfo.owner}</p>
                  <p className="text-gray-600">{companyInfo.address}</p>
                </div>

                <div className="pt-4 border-t">
                  <p className="text-sm text-gray-500 mb-2">Telefon</p>
                  <a 
                    href={`tel:${companyInfo.phone}`} 
                    className="flex items-center gap-3 text-[#1e3a5f] hover:text-[#c41e3a] transition-colors text-lg font-medium"
                  >
                    <div className="w-10 h-10 bg-[#1e3a5f] rounded-full flex items-center justify-center">
                      <Phone className="w-5 h-5 text-white" />
                    </div>
                    {companyInfo.phone}
                  </a>
                </div>

                <div className="pt-4 border-t">
                  <p className="text-sm text-gray-500 mb-2">E-Mail</p>
                  <a 
                    href={`mailto:${companyInfo.email}`} 
                    className="flex items-center gap-3 text-[#1e3a5f] hover:text-[#c41e3a] transition-colors text-lg font-medium"
                  >
                    <div className="w-10 h-10 bg-[#1e3a5f] rounded-full flex items-center justify-center">
                      <Mail className="w-5 h-5 text-white" />
                    </div>
                    {companyInfo.email}
                  </a>
                </div>

                <div className="pt-4 border-t">
                  <p className="text-sm text-gray-500 mb-2">Einzugsgebiet</p>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-[#1e3a5f] rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-[#1e3a5f] font-medium">{companyInfo.region}</p>
                      <p className="text-gray-600 text-sm">{companyInfo.extendedRegion}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* WhatsApp QR Code */}
            <div className="bg-[#f8fafc] rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-[#1e3a5f] mb-4">WhatsApp Kontakt</h2>
              <p className="text-gray-600 mb-6">
                Scannen Sie den QR-Code für direkten WhatsApp-Kontakt
              </p>
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <img
                  src={companyInfo.whatsappQR}
                  alt="WhatsApp QR Code"
                  className="w-full max-w-[280px] mx-auto h-auto"
                />
              </div>
              <p className="text-gray-500 text-sm mt-4 text-center">
                Schnelle und unkomplizierte Kommunikation via WhatsApp
              </p>
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-12 bg-[#1e3a5f] rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold text-white mb-4">So erreichen Sie mich</h3>
            <p className="text-blue-100 max-w-2xl mx-auto">
              Sie können mich jederzeit per Telefon, E-Mail oder WhatsApp kontaktieren. 
              Ich melde mich schnellstmöglich bei Ihnen zurück und freue mich auf Ihre Anfrage!
            </p>
            <div className="flex flex-wrap justify-center gap-4 mt-6">
              <a 
                href={`tel:${companyInfo.phone}`}
                className="bg-white text-[#1e3a5f] px-6 py-3 rounded-full font-medium hover:bg-gray-100 transition-colors flex items-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Anrufen
              </a>
              <a 
                href={`mailto:${companyInfo.email}`}
                className="bg-[#c41e3a] text-white px-6 py-3 rounded-full font-medium hover:bg-[#a31830] transition-colors flex items-center gap-2"
              >
                <Mail className="w-5 h-5" />
                E-Mail senden
              </a>
            </div>
          </div>

          {/* Kundenbewertungen Einleitung */}
          <div className="mt-12 bg-[#f8fafc] rounded-2xl p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="flex gap-1">
                {[1,2,3,4,5].map((star) => (
                  <Star key={star} className="w-8 h-8 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
            </div>
            <h3 className="text-2xl font-bold text-[#1e3a5f] mb-4">Meine Kundenbewertungen</h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Transparenz ist mir wichtig. Schauen Sie sich gerne an, was meine Kunden über meine Arbeit sagen – 
              ihre ehrlichen Bewertungen finden Sie auf den folgenden Plattformen.
            </p>
          </div>

          {/* Bewertungen & Badges */}
          <div className="mt-12 grid md:grid-cols-2 gap-8">
            {/* Google Bewertungen Widget */}
            <div className="bg-white rounded-2xl p-8 shadow-md">
              <h3 className="text-xl font-bold text-[#1e3a5f] mb-4 text-center">Google Bewertungen</h3>
              <div 
                className="elfsight-app-3ae7e900-1aa8-4e23-9ec7-93b58f6ef324" 
                data-elfsight-app-lazy
              ></div>
            </div>

            {/* Renovero Badge */}
            <div className="bg-white rounded-2xl p-8 shadow-md text-center flex flex-col justify-center">
              <h3 className="text-xl font-bold text-[#1e3a5f] mb-4">Renovero Bewertungen</h3>
              <a 
                href="https://www.renovero.ch/de/handwerker/helper-schweiz-reinhardt-1"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
              >
                <img 
                  src="https://rest-api.renovero.ch/public/account/50E9Z3TG/rating-badge/image" 
                  alt="Renovero Bewertungen Helper-Schweiz"
                  className="mx-auto"
                />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
